import React, { useState, useEffect, useCallback } from "react";
import { CheckIcon } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchAdminCreateUserFormDetails } from "../../api/fetchAdminCreateUserFormDetails";
import { Role } from "../../api/fetchAdminCreateUserFormDetails/types";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Entity type for company selection
interface CompanyEntity {
  id: string;
  name: string;
  isCertainPath: boolean;
}

interface AdminUserFiltersProps {
  filters: {
    firstName?: string;
    lastName?: string;
    email?: string;
    salesforceId?: string;
    ssoId?: string;
    companyUuid?: string;
    businessRoleId?: number;
  };
  onFilterChange: (
    filterKey: string,
    value: string | number | undefined,
  ) => void;
}

const AdminUserFilters: React.FC<AdminUserFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [firstNameFilter, setFirstNameFilter] = useState(
    filters.firstName || "",
  );
  const [lastNameFilter, setLastNameFilter] = useState(filters.lastName || "");
  const [emailFilter, setEmailFilter] = useState(filters.email || "");
  const [salesforceIdFilter, setSalesforceIdFilter] = useState(
    filters.salesforceId || "",
  );
  const [ssoIdFilter, setSsoIdFilter] = useState(filters.ssoId || "");
  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(
    null,
  );
  const [roleFilter, setRoleFilter] = useState(
    filters.businessRoleId?.toString() || "",
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const debouncedFirstNameFilter = useDebouncedValue(firstNameFilter, 500);
  const debouncedLastNameFilter = useDebouncedValue(lastNameFilter, 500);
  const debouncedEmailFilter = useDebouncedValue(emailFilter, 500);
  const debouncedSalesforceIdFilter = useDebouncedValue(
    salesforceIdFilter,
    500,
  );
  const debouncedSsoIdFilter = useDebouncedValue(ssoIdFilter, 500);

  // Fetch companies for EntitySingleSelect with search and pagination
  const fetchCompaniesForSelect = useCallback(
    async ({
      searchTerm,
      page,
    }: {
      searchTerm?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const result = await fetchCompanies({
        searchTerm,
        page,
        pageSize: 500,
        sortBy: "companyName",
        sortOrder: "ASC",
      });
      const { companies } = result.data;
      const totalCount = result.meta?.totalCount || companies.length;
      return {
        data: companies.map((c) => ({
          id: c.uuid,
          name: c.companyName,
          isCertainPath: c.isCertainPath || false,
        })),
        totalCount,
      };
    },
    [],
  );

  // Custom row renderer to show CP badge
  const renderCompanyRow = useCallback(
    (
      company: CompanyEntity,
      isSelected: boolean,
      toggle: (ent: CompanyEntity) => void,
    ) => (
      <button
        className={`w-full px-3 py-2 text-left transition-colors ${
          isSelected
            ? "bg-gray-100 dark:bg-gray-800"
            : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
        }`}
        onClick={() => toggle(company)}
        type="button"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
              {company.name}
            </span>
            {company.isCertainPath && (
              <span className="text-[10px] bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                CP
              </span>
            )}
          </div>
          {isSelected && (
            <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
          )}
        </div>
      </button>
    ),
    [],
  );

  // Fetch roles for dropdown
  useEffect(() => {
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await fetchAdminCreateUserFormDetails();
        setRoles(response.data.availableRoles);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    onFilterChange("firstName", debouncedFirstNameFilter);
  }, [debouncedFirstNameFilter]);

  useEffect(() => {
    onFilterChange("lastName", debouncedLastNameFilter);
  }, [debouncedLastNameFilter]);

  useEffect(() => {
    onFilterChange("email", debouncedEmailFilter);
  }, [debouncedEmailFilter]);

  useEffect(() => {
    onFilterChange("salesforceId", debouncedSalesforceIdFilter);
  }, [debouncedSalesforceIdFilter]);

  useEffect(() => {
    onFilterChange("ssoId", debouncedSsoIdFilter);
  }, [debouncedSsoIdFilter]);

  const handleCompanyChange = useCallback(
    (company: CompanyEntity | null) => {
      setSelectedCompany(company);
      onFilterChange("companyUuid", company?.id || undefined);
    },
    [onFilterChange],
  );

  const handleClearAllFilters = useCallback(() => {
    setFirstNameFilter("");
    setLastNameFilter("");
    setEmailFilter("");
    setSalesforceIdFilter("");
    setSsoIdFilter("");
    setSelectedCompany(null);
    setRoleFilter("");
    onFilterChange("firstName", undefined);
    onFilterChange("lastName", undefined);
    onFilterChange("email", undefined);
    onFilterChange("salesforceId", undefined);
    onFilterChange("ssoId", undefined);
    onFilterChange("companyUuid", undefined);
    onFilterChange("businessRoleId", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    firstNameFilter ||
    lastNameFilter ||
    emailFilter ||
    salesforceIdFilter ||
    ssoIdFilter ||
    selectedCompany ||
    roleFilter;

  const handleRoleChange = (value: string) => {
    if (value === "all") {
      setRoleFilter("");
      onFilterChange("businessRoleId", undefined);
    } else {
      setRoleFilter(value);
      onFilterChange("businessRoleId", parseInt(value, 10));
    }
  };

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Company Searchable Select */}
        <div>
          <Label htmlFor="company">Company</Label>
          <div className="mt-1.5">
            <EntitySingleSelect<CompanyEntity>
              entityNamePlural="Companies"
              entityNameSingular="Company"
              fetchEntities={fetchCompaniesForSelect}
              onChange={handleCompanyChange}
              placeholder="All Companies"
              renderEntityRow={renderCompanyRow}
              value={selectedCompany}
            />
          </div>
        </div>

        {/* Role Dropdown */}
        <div>
          <Label htmlFor="role">Role</Label>
          <Select
            disabled={loadingRoles}
            onValueChange={handleRoleChange}
            value={roleFilter || "all"}
          >
            <SelectTrigger className="mt-1.5 bg-white" id="role">
              <SelectValue
                placeholder={loadingRoles ? "Loading..." : "All Roles"}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* First Name */}
        <div>
          <Label htmlFor="firstName">First Name</Label>
          <Input
            className="mt-1.5 bg-white"
            id="firstName"
            onChange={(e) => setFirstNameFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={firstNameFilter}
          />
        </div>

        {/* Last Name */}
        <div>
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            className="mt-1.5 bg-white"
            id="lastName"
            onChange={(e) => setLastNameFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={lastNameFilter}
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            className="mt-1.5 bg-white"
            id="email"
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={emailFilter}
          />
        </div>

        {/* Salesforce ID */}
        <div>
          <Label htmlFor="salesforceId">Salesforce ID</Label>
          <Input
            className="mt-1.5 bg-white"
            id="salesforceId"
            onChange={(e) => setSalesforceIdFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={salesforceIdFilter}
          />
        </div>

        {/* SSO ID */}
        <div>
          <Label htmlFor="ssoId">SSO ID</Label>
          <Input
            className="mt-1.5 bg-white"
            id="ssoId"
            onChange={(e) => setSsoIdFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={ssoIdFilter}
          />
        </div>

        {/* Clear All Filters */}
        <div className="flex items-end">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearAllFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminUserFilters;
